import { createSelector } from 'reselect';

import {
  getIsGoalkeeperFromProps,
  getPlayerIdFromProps,
  getSportIdFromProps,
  getTeamIdFromProps,
} from '../../../../selectors/_propsGetters_';
import { selectEntitiesCompetitors } from '../../../../selectors/entities/competitors/entitiesCompetitorsPrimary.selector';
import {
  selectEntitiesMatchesEntity,
  selectEntitiesMatchesEntityCompetitor1Id,
  selectEntitiesMatchesEntityCompetitor2Id,
} from '../../../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesPlayersEntityJerseyNumber } from '../../../../selectors/entities/players/entitiesPlayersPrimary.selector';
import { type CompetitorState } from '../../../../states/entities/competitors';
import { type SelectorMaker, type Selector } from '../../../../types/selectors';
import { SportIds } from '../../../../types/sports';
import { createShallowEqualSelector } from '../../../../utils/reselect';
import { SportsWithNoTab } from '../../constants/runtime';

import { LineUpPlayerNumberSize } from './LineUpPlayerJersey';

type LineUpPlayerJerseyPropsType = {
  number?: number;
  jerseyColor?: string;
  jerseyImg?: string;
  numberColor?: string;
  numberOutlineColor?: string;
  numberSize: LineUpPlayerNumberSize;
};

const makeLineUpPlayerJerseyPropsSelector: SelectorMaker<LineUpPlayerJerseyPropsType> = () => {
  const selectMatchTeamCompetitorId: Selector<number | undefined> = createSelector(
    [selectEntitiesMatchesEntity, getTeamIdFromProps],
    (match, teamId) => {
      if (teamId === 1) {
        return match?.competitor1Id;
      }
      if (teamId === 2) {
        return match?.competitor2Id;
      }
    },
  );

  const selectMatchTeamCompetitor: Selector<CompetitorState | undefined> = createShallowEqualSelector(
    [
      selectEntitiesMatchesEntityCompetitor1Id,
      selectEntitiesMatchesEntityCompetitor2Id,
      selectMatchTeamCompetitorId,
      selectEntitiesCompetitors,
      getSportIdFromProps,
      getPlayerIdFromProps,
    ],
    (competitor1, competitor2, competitorIdFromContext, competitors, sportId, playerId) => {
      if (
        playerId &&
        competitor1 &&
        competitor2 &&
        sportId &&
        (SportsWithNoTab.indexOf(sportId) !== -1 || sportId === SportIds.ICEHOCKEY)
      ) {
        return [competitor1, competitor2]
          .map((competitorId) => competitors[competitorId])
          .filter((competitor) => competitor)
          .find((competitor) => competitor?.players?.indexOf(playerId) !== -1);
      }
      return competitorIdFromContext ? competitors[competitorIdFromContext] : undefined;
    },
  );

  const selectTeamJerseyColor: Selector<string | undefined> = createSelector(selectMatchTeamCompetitor, (competitor) =>
    competitor?.jerseyBase ? `#${competitor.jerseyBase}` : undefined,
  );

  const selectTeamJerseyImg: Selector<string | undefined> = createSelector(
    [selectMatchTeamCompetitor, getIsGoalkeeperFromProps],
    (competitor, isGoalKeeper) =>
      isGoalKeeper ? competitor?.jersey?.goalkeeperImg ?? undefined : competitor?.jersey?.backImg ?? undefined,
  );

  const selectTeamNumberColor: Selector<string | undefined> = createSelector(
    [selectMatchTeamCompetitor, getIsGoalkeeperFromProps],
    (competitor, isGoalKeeper) => {
      if (isGoalKeeper) {
        return competitor?.jersey?.goalkeeperNumberColor ? `#${competitor.jersey.goalkeeperNumberColor}` : undefined;
      }
      return competitor?.jersey?.numberColor ? `#${competitor.jersey.numberColor}` : undefined;
    },
  );

  const selectTeamNumberOutlineColor: Selector<string | undefined> = createSelector(
    [selectMatchTeamCompetitor, getIsGoalkeeperFromProps],
    (competitor, isGoalKeeper) =>
      competitor?.jersey?.numberOutlineColor && !isGoalKeeper ? `#${competitor.jersey.numberOutlineColor}` : undefined,
  );

  return createSelector(
    [
      selectEntitiesPlayersEntityJerseyNumber,
      selectTeamJerseyColor,
      selectTeamJerseyImg,
      selectTeamNumberColor,
      selectTeamNumberOutlineColor,
      getSportIdFromProps,
    ],
    (number, jerseyColor, jerseyImg, numberColor, numberOutlineColor, sportId) => ({
      number,
      jerseyColor,
      jerseyImg,
      numberColor,
      numberOutlineColor,
      numberSize: sportId === SportIds.BASKETBALL ? LineUpPlayerNumberSize.small : LineUpPlayerNumberSize.medium,
    }),
  );
};

export default makeLineUpPlayerJerseyPropsSelector;
